import { buildControllerRules } from "@gettactic/helpers/src/hook-form/buildControllerRules";
import { buildErrorMessageForName } from "@gettactic/helpers/src/hook-form/buildErrorMessageForName";
import React, { useCallback, useRef } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { TextEditorInput } from "./TextEditorInput";
import {
  HookFormTextEditorInputProps,
  TextEditorInputPropsWithFormControl
} from "./TextEditorInput.types";

interface TextEditorFieldProps
  extends Omit<TextEditorInputPropsWithFormControl, "onChange"> {
  value: string;
  onChange: (value: string | null) => void;
}

const TextEditorField: React.FC<TextEditorFieldProps> = ({
  value,
  onChange,
  ...rest
}) => {
  const isInitialRender = useRef(true);

  const handleChange = useCallback(
    (newValue: string | null) => {
      // Skip the initial onChange triggered by TipTap editor initialization
      // to prevent marking the form as dirty on mount
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }
      onChange(newValue);
    },
    [onChange]
  );

  return (
    <TextEditorInput value={value || ""} onChange={handleChange} {...rest} />
  );
};

/**
 * `TextEditorInput` bound to a react-hook-form field holding the HTML (or `null`).
 */
export const HookFormTextEditorInput = <T extends FieldValues>(
  props: HookFormTextEditorInputProps<T>
): React.ReactElement => {
  const { form, name, rules, ...rest } = props;

  return (
    <Controller
      control={form.control}
      name={name}
      rules={buildControllerRules({
        form,
        name,
        rules,
        isRequired: props.isRequired
      })}
      render={({ field: { onChange, value } }) => (
        <TextEditorField
          value={value || ""}
          onChange={onChange}
          error={buildErrorMessageForName(form, name)}
          {...rest}
        />
      )}
    />
  );
};
